import { ButtonSearch } from '@/components/common/Button';
import Combobox from '@/components/common/Combobox';
import { Form, FormControl, FormField, FormItem } from '@/components/common/Form';
import { BIKE_STATUS_CONFIG, ROLE_EK_OPTION_SELECT, ROLE_GAS_OPTION_SELECT } from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import { ROLE_USER } from '@/types/enums';
import { isPathEk } from '@/utils/routes';
import { observer } from 'mobx-react-lite';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

interface FilterProps {
    form: UseFormReturn<any>,
    isInModal?: boolean,
    onSearch: (searchParams?: any) => void
}

export default observer(function FilterForm({ form, isInModal = false, onSearch }: FilterProps) {
    // hooks
    const { t } = useTranslation();
    const { pathname } = useLocation();

    // const
    const roles = isPathEk(pathname) ?
        ROLE_EK_OPTION_SELECT.filter(role => role.id !== ROLE_USER.RENTAL_BIKE_OWNER_EK && role.id !== ROLE_USER.SYSTEM_ADMIN) :
        ROLE_GAS_OPTION_SELECT.filter(role => role.id !== ROLE_USER.RENTAL_BIKE_OWNER_RD && role.id !== ROLE_USER.SYSTEM_ADMIN);

    // store
    const {
        accountStore: { accounts },
        capacitieStore: { capacitieList },
        bikeStore: { bikeModelListIncluesDeleted }
    } = useStore();

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSearch)}>
                <div className='layout-filter max-h-[75vh] overflow-y-auto'>
                    <FormField
                        control={form.control}
                        name='roleUserId'
                        render={({ field }) => (
                            <FormItem>
                                <Combobox
                                    search={false}
                                    placeholder={t('field.role')}
                                    value={field.value?.toString() ?? ''}
                                    onChange={(item) => {
                                        field.onChange(item?.code ?? '');
                                    }}
                                    items={
                                        roles.map(item => ({
                                            label: t(`${item.name}`),
                                            value: item.id?.toString(),
                                            code: item.id
                                        }))
                                    }
                                />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name='bikeOwnerId'
                        render={({ field }) => (
                            <FormItem>
                                <Combobox
                                    placeholder={t('field.owner')}
                                    value={field.value?.toString() ?? ''}
                                    onChange={(item) => {
                                        field.onChange(item?.code ?? '');
                                    }}
                                    items={
                                        (Array.isArray(accounts) && accounts?.length ?
                                            [
                                                ...accounts
                                                    .filter(account => (
                                                        Number(account?.role?.id) !== ROLE_USER.RENTAL_BIKE_OWNER_EK &&
                                                        Number(account?.role?.id) !== ROLE_USER.RENTAL_BIKE_OWNER_RD
                                                    ))
                                                    .map(item => ({
                                                        label: item.fullname,
                                                        labelSearch: item.fullname + item?.userCode,
                                                        value: item.id.toString(),
                                                        code: item.id
                                                    }))
                                            ] :
                                            []
                                        )
                                    }
                                />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name='vehicleStatus'
                        render={({ field }) => (
                            <FormItem>
                                <Combobox
                                    search={false}
                                    placeholder={t('placeholder.bike_status')}
                                    value={field.value?.toString() ?? ''}
                                    onChange={(item) => {
                                        field.onChange(item?.code ?? '');
                                    }}
                                    items={
                                        Object.values(BIKE_STATUS_CONFIG).map((item) => {
                                            return {
                                                label: t(`words_title.${item.key}`) ?? '',
                                                value: item.value.toString(),
                                                code: item.value
                                            };
                                        })
                                    }
                                />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name='bikeCapacityId'
                        render={({ field }) => (
                            <FormItem>
                                <Combobox
                                    placeholder={t('field.engine_size')}
                                    value={field.value?.toString() ?? ''}
                                    onChange={(item) => {
                                        field.onChange(item?.code ?? '');
                                    }}
                                    search={false}
                                    items={
                                        (Array.isArray(capacitieList) && capacitieList?.length ?
                                            [
                                                ...capacitieList.map(item => ({
                                                    label: t(`words_title.${item.code}`) ?? '',
                                                    value: item.id.toString(),
                                                    code: item.id
                                                }))
                                            ] :
                                            []
                                        )
                                    }
                                />
                            </FormItem>
                        )}
                    />
                    {!isInModal && (
                        <FormField
                            control={form.control}
                            name='bikeModel'
                            render={({ field }) => (
                                <FormItem>
                                    <FormControl>
                                        <Combobox
                                            placeholder={t('field.model_name')}
                                            value={field.value}
                                            onChange={(item) => {
                                                field.onChange(item?.value ?? '');
                                            }}
                                            search={true}
                                            items={
                                                (Array.isArray(bikeModelListIncluesDeleted) && bikeModelListIncluesDeleted?.length > 0 ?
                                                    [
                                                        ...bikeModelListIncluesDeleted.map(item => ({
                                                            label: item.bikeModel,
                                                            value: item.bikeModel,
                                                            code: item.bikeModel
                                                        }))
                                                    ] :
                                                    []
                                                )
                                            }
                                        />
                                    </FormControl>
                                </FormItem>
                            )}
                        />
                    )}

                    <div className='col-span-full flex justify-center'>
                        <ButtonSearch size='md' className='max-w-52'>
                            {t('button.search')}
                        </ButtonSearch>
                    </div>
                </div>
            </form>
        </Form>
    );
});
